import { AIR, BEDROCK, CHUNK_VOLUME, fbm2, hash32, Rng, type VoxelWorld } from '@vm/shared';
import { family, M } from '../palette';

/** A material, or a function choosing one per voxel (patterns, noise). */
export type Brush = number | ((x: number, y: number, z: number) => number);

export const pick = (shades: number[], h: number): number => shades[h % shades.length];

// Brushes ------------------------------------------------------------------------------

/**
 * Patterned surfaces (bricks, planks, stone blocks) are drawn by the world shader, so these
 * brushes return one constant material per call. Keeping surfaces uniform lets the greedy
 * mesher merge whole walls into a handful of quads. The seed picks a shade for variety
 * between buildings.
 */
export function bricks(shades: number[], seed = 1): Brush {
  return shades[seed % shades.length];
}

export function planks(shades: number[], seed = 2): Brush {
  return shades[seed % shades.length];
}

export function floorBoards(shades: number[], seed = 3): Brush {
  return shades[seed % shades.length];
}

export function blocks(shades: number[], _size = 3, seed = 4): Brush {
  return shades[seed % shades.length];
}

/** Per-voxel random shade (use sparingly: it prevents face merging). */
export function speckle(shades: number[], seed = 5): Brush {
  const hash = hash32;
  return (x, y, z) => shades[hash(x, y, z, seed) % shades.length];
}

/** Uniform shade: the shader's per-voxel hash already adds subtle variation. */
export function solid(shades: number[], seed = 0): Brush {
  return shades[seed % shades.length];
}

/** Low-frequency patches of shades (grass, dirt, sand). */
export function patches(shades: number[], scale = 0.12, seed = 6): Brush {
  const noise = fbm2;
  return (x, _y, z) => {
    const n = noise(x * scale, z * scale, seed, 2);
    const i = Math.min(shades.length - 1, Math.floor(n * shades.length * 1.3 - shades.length * 0.15));
    return shades[Math.max(0, i)];
  };
}

export function stripes(a: number, b: number, width: number, axis: 'x' | 'y' | 'z' = 'y'): Brush {
  return (x, y, z) => {
    const v = axis === 'x' ? x : axis === 'y' ? y : z;
    return Math.floor(v / width) % 2 === 0 ? a : b;
  };
}

export const brushAt = (b: Brush, x: number, y: number, z: number): number => (typeof b === 'number' ? b : b(x, y, z));

// Symmetry -------------------------------------------------------------------------------

/**
 * How `MapBuilder.symmetrize` completes a map from its low half:
 * mirrorX / mirrorZ reflect the half x < sx/2 (z < sz/2) across the center line;
 * rotateX / rotateZ copy that half rotated 180 degrees about the map center.
 */
export type Symmetry = 'mirrorX' | 'mirrorZ' | 'rotateX' | 'rotateZ';

/** Material pairs exchanged when one team's half is copied to the other side. */
export function teamSwaps(extra: [string, string][] = []): [number, number][] {
  const pairs: [string, string][] = [['team_red', 'team_blue'], ['team_red_glow', 'team_blue_glow'], ['core_red', 'core_blue'], ...extra];
  return pairs.map(([a, b]) => [M(a), M(b)]);
}

/** Symmetric counterpart of a point in meters on a map of size sxm x szm meters. */
export function symPoint(kind: Symmetry, sxm: number, szm: number, x: number, z: number): [number, number] {
  if (kind === 'mirrorX') return [sxm - x, z];
  if (kind === 'mirrorZ') return [x, szm - z];
  return [sxm - x, szm - z];
}

/** Yaw (radians, 0 = facing -Z) of a spawn after applying the symmetry. */
export function symYaw(kind: Symmetry, yaw: number): number {
  if (kind === 'mirrorX') return -yaw;
  if (kind === 'mirrorZ') return Math.PI - yaw;
  return yaw + Math.PI;
}

/** Yaw that faces from (x, z) toward (tx, tz), in meters. */
export function yawToward(x: number, z: number, tx: number, tz: number): number {
  return Math.atan2(-(tx - x), -(tz - z));
}

// Terrain --------------------------------------------------------------------------------

/**
 * Samples `fn` every `step` voxels over an sx x sz area and returns a bilinear lookup: smooth
 * noise fields cost a fraction of evaluating fbm per column.
 */
export function smoothField(sx: number, sz: number, step: number, fn: (x: number, z: number) => number): (x: number, z: number) => number {
  const gx = Math.floor(sx / step) + 2;
  const gz = Math.floor(sz / step) + 2;
  const g = new Float64Array(gx * gz);
  for (let j = 0; j < gz; j++) for (let i = 0; i < gx; i++) g[i + j * gx] = fn(i * step, j * step);
  return (x, z) => {
    const fx = x / step;
    const fz = z / step;
    const i = Math.max(0, Math.min(gx - 2, Math.floor(fx)));
    const j = Math.max(0, Math.min(gz - 2, Math.floor(fz)));
    const tx = fx - i;
    const tz = fz - j;
    const k = i + j * gx;
    const top = g[k] + (g[k + 1] - g[k]) * tx;
    const bot = g[k + gx] + (g[k + gx + 1] - g[k + gx]) * tx;
    return top + (bot - top) * tz;
  };
}

export interface TerrainSpec {
  /** First air y per column (index x + z * sx). Columns <= 0 hold no ground, only water (if any). */
  height: Int16Array;
  /** Top voxel material: one for every column, or one per column. */
  surface: number | Uint8Array;
  /** Material under the surface; a list is picked per voxel by hash (only visible once dug). */
  sub: number | number[];
  /** Per-column sub material (overrides `sub`). */
  subCol?: Uint8Array;
  deep: number;
  subDepth?: number;
  /** Indestructible floor under every ground column (default true). */
  bedrock?: boolean;
  /** First y above the bedrock (default 2: bedrock is y 0-1). */
  bedrockTop?: number;
  /** Only columns higher than this get bedrock (default 0). */
  bedrockMin?: number;
  water?: number;
  /** First air y above the water. */
  waterLevel?: number;
}

// Builder --------------------------------------------------------------------------------

export type Side = 'n' | 's' | 'e' | 'w';

/** A staircase as built: first step cell (x, z) at base y, climbing toward `dir`. */
export interface StairRecord {
  x: number;
  y: number;
  z: number;
  dir: Side;
  width: number;
  steps: number;
}

/** Debug hook: while `on`, every staircase built is recorded (tools/stairs-audit.ts). */
export const STAIR_AUDIT: { on: boolean; list: StairRecord[] } = { on: false, list: [] };

/**
 * The copy `symmetrize` makes of a staircase lying wholly in the source half (x or z below the
 * middle), or null for one that is not copied.
 */
function mirrorStair(r: StairRecord, flipX: boolean, flipZ: boolean, alongX: boolean, sx: number, sz: number): StairRecord | null {
  const ew = r.dir === 'e' || r.dir === 'w';
  // Largest cell coordinate of the flight along the split axis.
  const xb = ew ? (r.dir === 'e' ? r.x + r.steps - 1 : r.x) : r.x + r.width - 1;
  const zb = ew ? r.z + r.width - 1 : r.dir === 's' ? r.z + r.steps - 1 : r.z;
  if (alongX ? xb >= sx / 2 : zb >= sz / 2) return null;
  const flip: Record<Side, Side> = { e: 'w', w: 'e', n: 's', s: 'n' };
  let { x, z, dir } = r;
  if (flipX) {
    if (ew) {
      x = sx - 1 - r.x;
      dir = flip[dir];
    } else x = sx - r.x - r.width;
  }
  if (flipZ) {
    if (!ew) {
      z = sz - 1 - r.z;
      dir = flip[dir];
    } else z = sz - r.z - r.width;
  }
  return { x, y: r.y, z, dir, width: r.width, steps: r.steps };
}

export interface WindowSpec {
  w: number;
  h: number;
  sill: number;
  spacing: number;
  glass: number | null;
}

export interface DoorSpec {
  side: Side;
  /** Offset along the wall from its start, voxels; defaults to centered. */
  at?: number;
  w?: number;
  h?: number;
}

export interface BuildingSpec {
  x0: number;
  z0: number;
  x1: number;
  z1: number;
  y0: number;
  floors: number;
  floorH?: number;
  wallT?: number;
  wall: Brush;
  floor?: Brush;
  trim?: Brush;
  roof?: Brush;
  roofStyle?: 'flat' | 'gable' | 'gableX' | 'none';
  windows?: WindowSpec | null;
  doors?: DoorSpec[];
  stairs?: boolean;
  /** Extra decorative band at each floor line. */
  band?: Brush;
  parapet?: number;
}

export class MapBuilder {
  readonly rng: Rng;
  constructor(
    readonly w: VoxelWorld,
    readonly seed: number,
  ) {
    this.rng = new Rng(seed);
  }

  get sx(): number {
    return this.w.sx;
  }
  get sy(): number {
    return this.w.sy;
  }
  get sz(): number {
    return this.w.sz;
  }

  set(x: number, y: number, z: number, b: Brush): void {
    this.w.set(x, y, z, brushAt(b, x, y, z));
  }

  /** Half-open box fill. */
  box(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, b: Brush): void {
    if (typeof b === 'number') {
      this.w.fillBox(x0, y0, z0, x1, y1, z1, b);
      return;
    }
    x0 = Math.max(0, x0);
    y0 = Math.max(0, y0);
    z0 = Math.max(0, z0);
    x1 = Math.min(this.sx, x1);
    y1 = Math.min(this.sy, y1);
    z1 = Math.min(this.sz, z1);
    for (let y = y0; y < y1; y++) for (let z = z0; z < z1; z++) for (let x = x0; x < x1; x++) this.w.set(x, y, z, b(x, y, z));
  }

  clear(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): void {
    this.w.fillBox(x0, y0, z0, x1, y1, z1, AIR);
  }

  /** Only fills cells that are currently air. */
  boxIfAir(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, b: Brush): void {
    for (let y = Math.max(0, y0); y < Math.min(this.sy, y1); y++)
      for (let z = Math.max(0, z0); z < Math.min(this.sz, z1); z++)
        for (let x = Math.max(0, x0); x < Math.min(this.sx, x1); x++) if (this.w.get(x, y, z) === AIR) this.w.set(x, y, z, brushAt(b, x, y, z));
  }

  /** Four walls of thickness t around a half-open footprint. */
  walls(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, t: number, b: Brush): void {
    this.box(x0, y0, z0, x1, y1, z0 + t, b);
    this.box(x0, y0, z1 - t, x1, y1, z1, b);
    this.box(x0, y0, z0 + t, x0 + t, y1, z1 - t, b);
    this.box(x1 - t, y0, z0 + t, x1, y1, z1 - t, b);
  }

  cylinder(cx: number, cz: number, y0: number, y1: number, r: number, b: Brush, inner = -1): void {
    const r2 = r * r;
    const i2 = inner >= 0 ? inner * inner : -1;
    const R = Math.ceil(r);
    for (let dz = -R; dz <= R; dz++) {
      for (let dx = -R; dx <= R; dx++) {
        const d2 = dx * dx + dz * dz;
        if (d2 > r2 || d2 < i2) continue;
        for (let y = y0; y < y1; y++) this.set(cx + dx, y, cz + dz, b);
      }
    }
  }

  sphere(cx: number, cy: number, cz: number, r: number, b: Brush, onlyAir = false): void {
    const r2 = r * r;
    const R = Math.ceil(r);
    for (let dy = -R; dy <= R; dy++)
      for (let dz = -R; dz <= R; dz++)
        for (let dx = -R; dx <= R; dx++) {
          if (dx * dx + dy * dy + dz * dz > r2) continue;
          const x = cx + dx;
          const y = cy + dy;
          const z = cz + dz;
          if (onlyAir && this.w.get(x, y, z) !== AIR) continue;
          this.set(x, y, z, b);
        }
  }

  /** Ellipsoid with a noisy surface (tree canopies, boulders). */
  blob(cx: number, cy: number, cz: number, rx: number, ry: number, rz: number, b: Brush, noise = 0.25, onlyAir = true): void {
    const seed = this.rng.u32();
    const hash = hash32;
    const air = AIR;
    for (let dy = -Math.ceil(ry); dy <= Math.ceil(ry); dy++)
      for (let dz = -Math.ceil(rz); dz <= Math.ceil(rz); dz++)
        for (let dx = -Math.ceil(rx); dx <= Math.ceil(rx); dx++) {
          const d = (dx * dx) / (rx * rx) + (dy * dy) / (ry * ry) + (dz * dz) / (rz * rz);
          if (d > 1) continue;
          const x = cx + dx;
          const y = cy + dy;
          const z = cz + dz;
          const n = (hash(x, y, z, seed) / 4294967296) * noise;
          if (d > 1 - n) continue;
          if (onlyAir && this.w.get(x, y, z) !== air) continue;
          this.set(x, y, z, b);
        }
  }

  /** Bedrock floor plus layered ground up to `top` (exclusive). */
  ground(top: number, surface: Brush, sub: Brush, deep: Brush): void {
    this.box(0, 0, 0, this.sx, 2, this.sz, BEDROCK);
    this.box(0, 2, 0, this.sx, Math.max(2, top - 6), this.sz, deep);
    this.box(0, Math.max(2, top - 6), 0, this.sx, top - 1, this.sz, sub);
    this.box(0, top - 1, 0, this.sx, top, this.sz, surface);
  }

  /** Terrain from a height function, filling columns above `base` (layered materials). */
  terrain(base: number, height: (x: number, z: number) => number, surface: Brush, sub: Brush, subDepth = 5): void {
    for (let z = 0; z < this.sz; z++) {
      for (let x = 0; x < this.sx; x++) {
        const h = Math.floor(height(x, z));
        if (h <= base) continue;
        for (let y = base; y < h; y++) this.set(x, y, z, y >= h - 1 ? surface : y >= h - subDepth ? sub : sub);
      }
    }
  }

  /** Solid 45-degree staircase: `steps` steps along +x (dir 1) or -x (dir -1), 1 voxel rise per voxel. */
  stairsX(x: number, y: number, z0: number, z1: number, steps: number, dir: 1 | -1, b: Brush): void {
    if (STAIR_AUDIT.on && steps > 0) STAIR_AUDIT.list.push({ x, y, z: z0, dir: dir === 1 ? 'e' : 'w', width: z1 - z0, steps });
    for (let i = 0; i < steps; i++) {
      const xx = x + i * dir;
      this.box(xx, y, z0, xx + 1, y + i + 1, z1, b);
    }
  }

  stairsZ(x0: number, x1: number, y: number, z: number, steps: number, dir: 1 | -1, b: Brush): void {
    if (STAIR_AUDIT.on && steps > 0) STAIR_AUDIT.list.push({ x: x0, y, z, dir: dir === 1 ? 's' : 'n', width: x1 - x0, steps });
    for (let i = 0; i < steps; i++) {
      const zz = z + i * dir;
      this.box(x0, y, zz, x1, y + i + 1, zz + 1, b);
    }
  }

  /** A multi-story building with windows, doors, interior stairs and a roof. */
  building(s: BuildingSpec): { top: number } {
    const floorH = s.floorH ?? 30;
    const t = s.wallT ?? 2;
    const floorB = s.floor ?? floorBoards(family('planks'));
    const { x0, z0, x1, z1, y0 } = s;
    const top = y0 + floorH * s.floors;

    // Clear the interior volume first so buildings can be placed over terrain.
    this.clear(x0, y0 + 1, z0, x1, top + 1, z1);
    // Foundation.
    this.box(x0, y0 - 2, z0, x1, y0 + 1, z1, s.trim ?? floorB);

    for (let f = 0; f < s.floors; f++) {
      const fy = y0 + f * floorH;
      if (f > 0) this.box(x0, fy - 1, z0, x1, fy + 1, z1, floorB);
      this.walls(x0, fy + 1, z0, x1, fy + floorH, z1, t, s.wall);
      if (s.band) {
        this.walls(x0, fy, z0, x1, fy + 1, z1, t, s.band);
      }
      // Windows.
      const win = s.windows === undefined ? { w: 8, h: 10, sill: 9, spacing: 18, glass: M('glass') } : s.windows;
      if (win) this.cutWindows(x0, z0, x1, z1, fy + 1 + win.sill, win, t, f === 0 ? s.doors ?? [] : []);
    }

    // Doors (ground floor).
    for (const d of s.doors ?? []) this.cutDoor(x0, z0, x1, z1, y0 + 1, d, t);

    // Stairs between floors, alternating between the north and south walls. A clear landing at the
    // foot lets players walk straight onto the first step; the run climbs +x to an opening in the
    // floor above and exits onto that floor (tools/stairs-audit.ts checks every staircase).
    if ((s.stairs ?? true) && s.floors > 1) {
      const steps = floorH - 1;
      const width = 12;
      const inner = x1 - x0 - 2 * t;
      // Landing: 0.8 m when it fits, never under 0.7 m (the player is 0.6 m wide); 0.6 m to step off on top.
      const land = Math.min(8, inner - steps - 6);
      const canFit = land >= 7 && z1 - z0 - 2 * t >= 2 * width + 2;
      for (let f = 0; canFit && f < s.floors - 1; f++) {
        const fy = y0 + f * floorH + 1;
        const north = f % 2 === 0;
        const sz0 = north ? z0 + t : z1 - t - width;
        const sz1 = north ? z0 + t + width : z1 - t;
        const startX = x0 + t + land;
        this.clear(x0 + t, fy, sz0, startX, fy + floorH - 2, sz1);
        this.stairsX(startX, fy, sz0, sz1, steps, 1, floorB);
        // Opening in the slab above the run (head room while climbing).
        const slabY = fy + floorH - 2;
        this.clear(startX - 3, slabY, sz0, startX + steps, slabY + 2, sz1);
      }
    }

    // Roof.
    const roofB = s.roof ?? bricks(family('roof_red'));
    const style = s.roofStyle ?? 'gable';
    if (style === 'flat' || style === 'none') {
      if (style === 'flat') {
        this.box(x0, top, z0, x1, top + 2, z1, s.trim ?? roofB);
        const p = s.parapet ?? 4;
        if (p > 0) this.walls(x0, top + 2, z0, x1, top + 2 + p, z1, 1, s.trim ?? s.wall);
      }
      return { top: top + 2 };
    }
    this.box(x0, top, z0, x1, top + 1, z1, floorB);
    const alongX = style === 'gable' ? x1 - x0 >= z1 - z0 : false;
    const span = alongX ? z1 - z0 : x1 - x0;
    const layers = Math.ceil(span / 4) + 1;
    for (let k = 0; k < layers; k++) {
      const y = top + 1 + k;
      if (alongX) {
        const za = z0 - 1 + 2 * k;
        const zb = z1 + 1 - 2 * k;
        if (za >= zb) break;
        // Sloped surfaces (2 voxels deep) and filled gable ends.
        this.box(x0 - 1, y, za, x1 + 1, y + 1, Math.min(za + 3, zb), roofB);
        this.box(x0 - 1, y, Math.max(zb - 3, za), x1 + 1, y + 1, zb, roofB);
        this.box(x0, y, za + 3, x0 + t, y + 1, zb - 3, s.wall);
        this.box(x1 - t, y, za + 3, x1, y + 1, zb - 3, s.wall);
      } else {
        const xa = x0 - 1 + 2 * k;
        const xb = x1 + 1 - 2 * k;
        if (xa >= xb) break;
        this.box(xa, y, z0 - 1, Math.min(xa + 3, xb), y + 1, z1 + 1, roofB);
        this.box(Math.max(xb - 3, xa), y, z0 - 1, xb, y + 1, z1 + 1, roofB);
        this.box(xa + 3, y, z0, xb - 3, y + 1, z0 + t, s.wall);
        this.box(xa + 3, y, z1 - t, xb - 3, y + 1, z1, s.wall);
      }
    }
    return { top: top + layers };
  }

  private cutWindows(x0: number, z0: number, x1: number, z1: number, y: number, win: WindowSpec, t: number, doors: DoorSpec[]): void {
    const sides: [Side, number, number][] = [
      ['n', x0, x1],
      ['s', x0, x1],
      ['w', z0, z1],
      ['e', z0, z1],
    ];
    for (const [side, a0, a1] of sides) {
      const len = a1 - a0;
      const count = Math.floor((len - 6) / win.spacing);
      if (count <= 0) continue;
      const start = a0 + Math.floor((len - (count - 1) * win.spacing - win.w) / 2);
      for (let i = 0; i < count; i++) {
        const a = start + i * win.spacing;
        // Skip windows overlapping a door on this side.
        if (doors.some((d) => d.side === side && Math.abs(this.doorCenter(d, x0, z0, x1, z1) - (a + win.w / 2)) < (d.w ?? 10) / 2 + win.w / 2 + 2)) continue;
        this.opening(side, x0, z0, x1, z1, a, a + win.w, y, y + win.h, t, win.glass);
      }
    }
  }

  private doorCenter(d: DoorSpec, x0: number, z0: number, x1: number, z1: number): number {
    const along = d.side === 'n' || d.side === 's' ? [x0, x1] : [z0, z1];
    const w = d.w ?? 10;
    return d.at !== undefined ? along[0] + d.at + w / 2 : (along[0] + along[1]) / 2;
  }

  private cutDoor(x0: number, z0: number, x1: number, z1: number, y: number, d: DoorSpec, t: number): void {
    const w = d.w ?? 10;
    const h = d.h ?? 22;
    const c = this.doorCenter(d, x0, z0, x1, z1);
    const a = Math.round(c - w / 2);
    this.opening(d.side, x0, z0, x1, z1, a, a + w, y, y + h, t, null);
  }

  /** Cuts a rectangular hole through one wall; optionally fills its middle layer with glass. */
  opening(side: Side, x0: number, z0: number, x1: number, z1: number, a0: number, a1: number, y0: number, y1: number, t: number, glass: number | null): void {
    if (side === 'n' || side === 's') {
      const za = side === 'n' ? z0 : z1 - t;
      this.clear(a0, y0, za, a1, y1, za + t);
      if (glass !== null) this.box(a0, y0, za + (t >> 1), a1, y1, za + (t >> 1) + 1, glass);
    } else {
      const xa = side === 'w' ? x0 : x1 - t;
      this.clear(xa, y0, a0, xa + t, y1, a1);
      if (glass !== null) this.box(xa + (t >> 1), y0, a0, xa + (t >> 1) + 1, y1, a1, glass);
    }
  }

  // Props -------------------------------------------------------------------------------

  tree(x: number, y: number, z: number, height = 40, canopy = 14, leaves: Brush = speckle(family('leaves')), trunk: Brush = M('log_1')): void {
    this.box(x - 1, y, z - 1, x + 2, y + height, z + 2, trunk);
    this.blob(x, y + height, z, canopy, canopy * 0.8, canopy, leaves, 0.35);
    this.blob(x + (canopy >> 1), y + height - (canopy >> 2), z, canopy * 0.7, canopy * 0.6, canopy * 0.7, leaves, 0.35);
    this.blob(x - (canopy >> 1), y + height - (canopy >> 2), z + (canopy >> 2), canopy * 0.6, canopy * 0.5, canopy * 0.6, leaves, 0.35);
  }

  pine(x: number, y: number, z: number, height = 60, leaves: Brush = speckle([M('leaves_3'), M('leaves_1')]), snow = false): void {
    this.box(x - 1, y, z - 1, x + 2, y + height, z + 2, M('log_2'));
    for (let k = 0; k < 5; k++) {
      const ly = y + 14 + k * ((height - 14) / 5);
      const r = 12 - k * 2;
      for (let dy = 0; dy < 6; dy++) {
        this.cylinder(x, z, Math.floor(ly + dy), Math.floor(ly + dy) + 1, r - dy * 0.8, leaves);
      }
      if (snow) this.cylinder(x, z, Math.floor(ly + 6), Math.floor(ly + 7), r - 5, M('snow_1'));
    }
  }

  crate(x: number, y: number, z: number, size = 10): void {
    const edge = M('planks_dark_1');
    this.box(x, y, z, x + size, y + size, z + size, planks(family('planks')));
    // Dark frame on the edges.
    for (const [a, b] of [
      [0, 0],
      [size - 1, 0],
      [0, size - 1],
      [size - 1, size - 1],
    ]) {
      this.box(x + a, y, z + b, x + a + 1, y + size, z + b + 1, edge);
      this.box(x, y + a, z + b, x + size, y + a + 1, z + b + 1, edge);
      this.box(x + a, y + b, z, x + a + 1, y + b + 1, z + size, edge);
    }
  }

  barrel(x: number, y: number, z: number, color = M('paint_red')): void {
    this.cylinder(x, z, y, y + 9, 3.2, color);
    this.cylinder(x, z, y + 2, y + 3, 3.4, M('steel_dark_1'));
    this.cylinder(x, z, y + 6, y + 7, 3.4, M('steel_dark_1'));
  }

  lamp(x: number, y: number, z: number, height = 32, glow = M('neon_yellow')): void {
    this.box(x, y, z, x + 1, y + height, z + 1, M('steel_dark_1'));
    this.box(x - 1, y + height, z - 1, x + 2, y + height + 2, z + 2, glow);
    this.box(x - 1, y + height + 2, z - 1, x + 2, y + height + 3, z + 2, M('steel_dark_2'));
  }

  bench(x: number, y: number, z: number, alongX = true): void {
    const legs = M('steel_dark_1');
    const seat = planks(family('planks'));
    if (alongX) {
      this.box(x, y + 3, z, x + 14, y + 4, z + 4, seat);
      this.box(x, y + 4, z + 3, x + 14, y + 8, z + 4, seat);
      this.box(x + 1, y, z + 1, x + 2, y + 3, z + 3, legs);
      this.box(x + 12, y, z + 1, x + 13, y + 3, z + 3, legs);
    } else {
      this.box(x, y + 3, z, x + 4, y + 4, z + 14, seat);
      this.box(x + 3, y + 4, z, x + 4, y + 8, z + 14, seat);
      this.box(x + 1, y, z + 1, x + 3, y + 3, z + 2, legs);
      this.box(x + 1, y, z + 12, x + 3, y + 3, z + 13, legs);
    }
  }

  car(x: number, y: number, z: number, color: number, alongX = true): void {
    const [L, W] = [40, 18];
    const glass = M('glass_tint');
    const tire = M('rubber');
    const bx = alongX ? L : W;
    const bz = alongX ? W : L;
    this.box(x, y + 3, z, x + bx, y + 11, z + bz, color);
    // Cabin.
    if (alongX) {
      this.box(x + 10, y + 11, z + 1, x + 30, y + 18, z + W - 1, glass);
      this.box(x + 11, y + 18, z + 1, x + 29, y + 19, z + W - 1, color);
      for (const wx of [x + 5, x + L - 11]) for (const wz of [z - 1, z + W - 4]) this.box(wx, y, wz, wx + 7, y + 7, wz + 5, tire);
      this.box(x + L - 1, y + 7, z + 2, x + L, y + 9, z + 6, M('neon_yellow'));
      this.box(x + L - 1, y + 7, z + W - 6, x + L, y + 9, z + W - 2, M('neon_yellow'));
    } else {
      this.box(x + 1, y + 11, z + 10, x + W - 1, y + 18, z + 30, glass);
      this.box(x + 1, y + 18, z + 11, x + W - 1, y + 19, z + 29, color);
      for (const wz of [z + 5, z + L - 11]) for (const wx of [x - 1, x + W - 4]) this.box(wx, y, wz, wx + 5, y + 7, wz + 7, tire);
    }
  }

  fence(x0: number, z0: number, x1: number, z1: number, y: number, b: Brush = planks(family('planks'))): void {
    const alongX = Math.abs(x1 - x0) >= Math.abs(z1 - z0);
    if (alongX) {
      for (let x = Math.min(x0, x1); x < Math.max(x0, x1); x++) {
        if (x % 8 === 0) this.box(x, y, z0, x + 1, y + 11, z0 + 1, b);
        this.box(x, y + 4, z0, x + 1, y + 5, z0 + 1, b);
        this.box(x, y + 8, z0, x + 1, y + 9, z0 + 1, b);
      }
    } else {
      for (let z = Math.min(z0, z1); z < Math.max(z0, z1); z++) {
        if (z % 8 === 0) this.box(x0, y, z, x0 + 1, y + 11, z + 1, b);
        this.box(x0, y + 4, z, x0 + 1, y + 5, z + 1, b);
        this.box(x0, y + 8, z, x0 + 1, y + 9, z + 1, b);
      }
    }
  }

  /** Market stall with a striped cloth roof. */
  stall(x: number, y: number, z: number, cloth: number): void {
    const post = M('log_1');
    for (const [a, b] of [
      [0, 0],
      [19, 0],
      [0, 13],
      [19, 13],
    ])
      this.box(x + a, y, z + b, x + a + 1, y + 22, z + b + 1, post);
    this.box(x, y + 8, z, x + 20, y + 9, z + 14, planks(family('planks')));
    this.box(x - 1, y + 22, z - 1, x + 21, y + 23, z + 15, stripes(cloth, M('cloth_white'), 3, 'x'));
    this.box(x + 2, y + 9, z + 2, x + 6, y + 12, z + 6, M('pumpkin'));
    this.box(x + 8, y + 9, z + 3, x + 12, y + 11, z + 7, M('paint_lime'));
  }

  fountain(cx: number, y: number, cz: number, r = 22): void {
    const stone = blocks(family('marble'), 3);
    this.cylinder(cx, cz, y, y + 6, r, stone);
    this.cylinder(cx, cz, y + 1, y + 6, r - 3, AIR);
    this.cylinder(cx, cz, y, y + 4, r - 3, M('water'));
    this.cylinder(cx, cz, y, y + 26, 3, stone);
    this.cylinder(cx, cz, y + 26, y + 28, 8, stone);
    this.cylinder(cx, cz, y + 28, y + 30, 5, M('water'));
  }

  /** Blocky statue of a Voxel Marine on a plinth (one world voxel per character voxel). */
  statue(cx: number, y: number, cz: number, b: Brush = M('gold')): void {
    this.box(cx - 8, y, cz - 8, cx + 8, y + 8, cz + 8, blocks(family('marble'), 4));
    const base = y + 8;
    this.box(cx - 4, base, cz - 2, cx, base + 12, cz + 2, b);
    this.box(cx, base, cz - 2, cx + 4, base + 12, cz + 2, b);
    this.box(cx - 4, base + 12, cz - 2, cx + 4, base + 24, cz + 2, b);
    this.box(cx - 8, base + 12, cz - 2, cx - 4, base + 24, cz + 2, b);
    this.box(cx + 4, base + 18, cz - 2, cx + 8, base + 32, cz + 2, b);
    this.box(cx - 4, base + 24, cz - 4, cx + 4, base + 32, cz + 4, b);
  }

  // Whole-map operations -----------------------------------------------------------------

  /**
   * Completes a symmetric map by copying its low half onto the other half (chunk by chunk, so it
   * is fast), exchanging team materials. Build one team's half first, symmetrize, then add
   * anything that straddles the center line.
   */
  symmetrize(kind: Symmetry, swaps: [number, number][] = teamSwaps()): void {
    const w = this.w;
    const lut = new Uint8Array(256);
    for (let i = 0; i < 256; i++) lut[i] = i;
    for (const [a, b] of swaps) {
      lut[a] = b;
      lut[b] = a;
    }
    const swapVals: number[] = [];
    for (const [a, b] of swaps) swapVals.push(a, b);
    const vol = CHUNK_VOLUME;
    const alongX = kind === 'mirrorX' || kind === 'rotateX';
    const flipX = kind !== 'mirrorZ';
    const flipZ = kind !== 'mirrorX';
    if ((alongX ? w.ncx : w.ncz) % 2 !== 0) throw new Error('symmetrize needs an even chunk count along the split axis');
    const hx = alongX ? w.ncx >> 1 : w.ncx;
    const hz = alongX ? w.ncz : w.ncz >> 1;
    if (STAIR_AUDIT.on) {
      // The copied half's staircases get audited too.
      const copies: StairRecord[] = [];
      for (const r of STAIR_AUDIT.list) {
        const m = mirrorStair(r, flipX, flipZ, alongX, w.sx, w.sz);
        if (m) copies.push(m);
      }
      STAIR_AUDIT.list.push(...copies);
    }
    for (let cy = 0; cy < w.ncy; cy++)
      for (let cz = 0; cz < hz; cz++)
        for (let cx = 0; cx < hx; cx++) {
          const src = w.chunks[w.chunkIndex(cx, cy, cz)];
          const dst = w.chunks[w.chunkIndex(flipX ? w.ncx - 1 - cx : cx, cy, flipZ ? w.ncz - 1 - cz : cz)];
          dst.dmg = null;
          dst.rev++;
          const s = src.data;
          if (s === null) {
            dst.data = null;
            dst.fill = lut[src.fill];
            continue;
          }
          const d = dst.data ?? new Uint8Array(vol);
          // Copy 32-voxel rows as eight 32-bit words (byte-swapped and reversed when flipping x).
          const s32 = new Uint32Array(s.buffer, s.byteOffset, vol >> 2);
          const d32 = new Uint32Array(d.buffer, d.byteOffset, vol >> 2);
          for (let ly = 0; ly < 32; ly++)
            for (let lz = 0; lz < 32; lz++) {
              const si = ((lz << 5) | (ly << 10)) >> 2;
              const di = (((flipZ ? 31 - lz : lz) << 5) | (ly << 10)) >> 2;
              if (flipX)
                for (let k = 0; k < 8; k++) {
                  const v = s32[si + k];
                  d32[di + 7 - k] = ((v & 0xff) << 24) | ((v & 0xff00) << 8) | ((v >>> 8) & 0xff00) | (v >>> 24);
                }
              else for (let k = 0; k < 8; k++) d32[di + k] = s32[si + k];
            }
          // Team materials are rare: only chunks that hold one get the per-voxel swap.
          let has = false;
          for (let k = 0; k < swapVals.length && !has; k++) if (s.indexOf(swapVals[k]) >= 0) has = true;
          if (has) for (let i = 0; i < vol; i++) d[i] = lut[d[i]];
          dst.data = d;
        }
  }

  /**
   * Fills the world from a heightmap with layered ground (bedrock, deep, sub, surface) and
   * optional water, writing chunk storage directly. Call it first: it replaces the chunks it
   * touches. Chunks that end up a single material stay compact.
   */
  heightfield(t: TerrainSpec): void {
    const w = this.w;
    const sx = w.sx;
    const H = t.height;
    const subDepth = t.subDepth ?? 4;
    const water = t.water ?? AIR;
    const lvl = t.water !== undefined ? (t.waterLevel ?? 0) : 0;
    const bed = t.bedrock ?? true;
    const surfArr = typeof t.surface === 'number' ? null : t.surface;
    const surf0 = typeof t.surface === 'number' ? t.surface : 0;
    const subArr = typeof t.sub === 'number' ? null : t.sub;
    const sub0 = typeof t.sub === 'number' ? t.sub : 0;
    const deep = t.deep;
    const bedTop = t.bedrockTop ?? 2;
    const bedMin = t.bedrockMin ?? 0;
    const subCol = t.subCol ?? null;
    // Imported bindings are getter calls under some module loaders: keep hot values local.
    const air = AIR;
    const bedrock = BEDROCK;
    const hash = hash32;
    for (let cz = 0; cz < w.ncz; cz++)
      for (let cx = 0; cx < w.ncx; cx++) {
        let minH = 1 << 30;
        let maxH = -(1 << 30);
        // One material for every solid layer of every column (e.g. a flat sand sea bed)?
        const row0 = (cx << 5) + (cz << 5) * sx;
        const m0 = surfArr === null ? surf0 : surfArr[row0];
        let single = subArr === null && (subCol === null ? sub0 : subCol[row0]) === m0 && deep === m0;
        for (let lz = 0; lz < 32; lz++) {
          const row = (cx << 5) + ((cz << 5) + lz) * sx;
          for (let lx = 0; lx < 32; lx++) {
            const h = H[row + lx];
            if (h < minH) minH = h;
            if (h > maxH) maxH = h;
            if (single && ((surfArr !== null && surfArr[row + lx] !== m0) || (subCol !== null && subCol[row + lx] !== m0))) single = false;
          }
        }
        const top = Math.min(w.sy, Math.max(maxH, lvl));
        for (let cy = 0; cy < w.ncy; cy++) {
          const y0 = cy << 5;
          if (y0 >= top) break;
          const c = w.chunks[w.chunkIndex(cx, cy, cz)];
          c.dmg = null;
          c.rev++;
          // Every column has bedrock / no column has bedrock.
          const allBed = bed && minH > bedMin;
          const noBed = !bed || maxH <= bedMin;
          if (allBed && y0 + 32 <= bedTop) {
            c.data = null;
            c.fill = bedrock;
            continue;
          }
          if (minH > 0 && (noBed || y0 >= bedTop) && y0 + 32 <= minH - 1 - subDepth) {
            c.data = null;
            c.fill = deep;
            continue;
          }
          if (single && minH > 0 && (noBed || y0 >= bedTop) && y0 + 32 <= minH) {
            c.data = null;
            c.fill = m0;
            continue;
          }
          if (maxH <= y0 && y0 + 32 <= lvl) {
            c.data = null;
            c.fill = water;
            continue;
          }
          // Mixed chunk: write each column as runs (bedrock, deep, sub, surface, water, air).
          const d = c.data ?? new Uint8Array(CHUNK_VOLUME);
          const y1 = y0 + 32;
          const run = (i: number, a: number, b: number, v: number): void => {
            const lo = a > y0 ? a : y0;
            const hi = b < y1 ? b : y1;
            for (let y = lo; y < hi; y++) d[i | ((y - y0) << 10)] = v;
          };
          for (let lz = 0; lz < 32; lz++) {
            const z = (cz << 5) + lz;
            const row = (cx << 5) + z * sx;
            for (let lx = 0; lx < 32; lx++) {
              const col = row + lx;
              const h = H[col];
              const i = lx | (lz << 5);
              let solidTop = 0;
              if (h > 0) {
                const bedEnd = bed && h > bedMin ? Math.min(bedTop, h) : 0;
                const subStart = Math.max(bedEnd, h - 1 - subDepth);
                run(i, 0, bedEnd, bedrock);
                run(i, bedEnd, subStart, deep);
                if (subStart < h - 1 && h - 1 >= y0 && subStart < y1) {
                  if (subCol !== null) run(i, subStart, h - 1, subCol[col]);
                  else if (subArr === null) run(i, subStart, h - 1, sub0);
                  else for (let y = Math.max(subStart, y0); y < Math.min(h - 1, y1); y++) d[i | ((y - y0) << 10)] = subArr[hash((cx << 5) + lx, y, z, 77) % subArr.length];
                }
                if (h - 1 >= bedEnd) run(i, h - 1, h, surfArr === null ? surf0 : surfArr[col]);
                solidTop = h;
              }
              run(i, solidTop, lvl, water);
              run(i, Math.max(solidTop, lvl), y1, air);
            }
          }
          c.data = d;
        }
      }
  }

  /** First air y above the highest solid voxel at or below `from` in column (x, z). */
  surfaceY(x: number, z: number, from = this.sy - 1): number {
    const w = this.w;
    for (let y = Math.min(from, this.sy - 1); y >= 0; y--) if (w.get(x, y, z) !== 0) return y + 1;
    return 0;
  }

  /** Walking height in meters at a point (meters), searching down from `fromM` meters. */
  groundAt(xm: number, zm: number, fromM = this.sy / 10): number {
    return this.surfaceY(Math.floor(xm * 10), Math.floor(zm * 10), Math.floor(fromM * 10)) / 10;
  }

  /** Height (meters) a player standing at (xm, zm) rests at: the highest surface under its footprint. */
  standAt(xm: number, zm: number, fromM = this.sy / 10): number {
    let top = 0;
    const x0 = Math.floor((xm - 0.3) * 10);
    const z0 = Math.floor((zm - 0.3) * 10);
    for (let z = z0; z <= z0 + 6; z++) for (let x = x0; x <= x0 + 6; x++) top = Math.max(top, this.surfaceY(x, z, Math.floor(fromM * 10)));
    return top / 10;
  }

  // Structural helpers ---------------------------------------------------------------------

  /** Thick 3D line: a cube of edge t at every step from (x0, y0, z0) to (x1, y1, z1). */
  line(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, b: Brush, t = 1): void {
    const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), Math.abs(z1 - z0));
    for (let i = 0; i <= n; i++) {
      const x = n === 0 ? x0 : x0 + Math.round(((x1 - x0) * i) / n);
      const y = n === 0 ? y0 : y0 + Math.round(((y1 - y0) * i) / n);
      const z = n === 0 ? z0 : z0 + Math.round(((z1 - z0) * i) / n);
      this.box(x, y, z, x + t, y + t, z + t, b);
    }
  }

  /**
   * Open lattice girder filling a half-open box and running along `axis`: four corner chords of
   * thickness t, a ring every `brace` voxels and zig-zag diagonals on all four faces.
   */
  lattice(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, axis: 'x' | 'y' | 'z', b: Brush, t = 2, brace = 12): void {
    const lo = [x0, y0, z0];
    const hi = [x1, y1, z1];
    const ai = axis === 'x' ? 0 : axis === 'y' ? 1 : 2;
    const ui = (ai + 1) % 3;
    const vi = (ai + 2) % 3;
    const p = [0, 0, 0];
    const q = [0, 0, 0];
    const put = (a0: number, a1: number, u0: number, u1: number, v0: number, v1: number): void => {
      p[ai] = a0;
      q[ai] = a1;
      p[ui] = u0;
      q[ui] = u1;
      p[vi] = v0;
      q[vi] = v1;
      this.box(p[0], p[1], p[2], q[0], q[1], q[2], b);
    };
    const diag = (a0: number, u0: number, v0: number, a1: number, u1: number, v1: number): void => {
      p[ai] = a0;
      p[ui] = u0;
      p[vi] = v0;
      q[ai] = a1;
      q[ui] = u1;
      q[vi] = v1;
      this.line(p[0], p[1], p[2], q[0], q[1], q[2], b, 1);
    };
    const A0 = lo[ai];
    const A1 = hi[ai];
    const U0 = lo[ui];
    const U1 = hi[ui];
    const V0 = lo[vi];
    const V1 = hi[vi];
    for (const [u, v] of [
      [U0, V0],
      [U1 - t, V0],
      [U0, V1 - t],
      [U1 - t, V1 - t],
    ])
      put(A0, A1, u, u + t, v, v + t);
    const ring = (a: number): void => {
      put(a, a + 1, U0, U1, V0, V0 + 1);
      put(a, a + 1, U0, U1, V1 - 1, V1);
      put(a, a + 1, U0, U0 + 1, V0, V1);
      put(a, a + 1, U1 - 1, U1, V0, V1);
    };
    let flip = false;
    for (let a = A0; a < A1 - 1; a += brace) {
      const e = Math.min(A1 - 1, a + brace);
      ring(a);
      const [s0, s1] = flip ? [e, a] : [a, e];
      diag(s0, U0, V0, s1, U1 - 1, V0);
      diag(s0, U0, V1 - 1, s1, U1 - 1, V1 - 1);
      diag(s0, U0, V0, s1, U0, V1 - 1);
      diag(s0, U1 - 1, V0, s1, U1 - 1, V1 - 1);
      flip = !flip;
    }
    ring(A1 - 1);
  }

  /**
   * Solid staircase of `steps` one-voxel steps climbing toward `dir` (e = +x, w = -x, s = +z,
   * n = -z). (x, z) is the first step's cell; the stairs extend `width` voxels along +z (e/w) or +x
   * (n/s). `thick` > 0 builds floating steps of that thickness instead of solid ones.
   */
  stairs(x: number, y: number, z: number, dir: Side, width: number, steps: number, b: Brush, thick = 0): void {
    if (STAIR_AUDIT.on && steps > 0) STAIR_AUDIT.list.push({ x, y, z, dir, width, steps });
    for (let i = 0; i < steps; i++) {
      const top = y + i + 1;
      const bot = thick > 0 ? Math.max(y, top - thick) : y;
      if (dir === 'e') this.box(x + i, bot, z, x + i + 1, top, z + width, b);
      else if (dir === 'w') this.box(x - i, bot, z, x - i + 1, top, z + width, b);
      else if (dir === 's') this.box(x, bot, z + i, x + width, top, z + i + 1, b);
      else this.box(x, bot, z - i, x + width, top, z - i + 1, b);
    }
  }

  /** Battlements: merlons along the outer edge of a rectangular wall top at height y. */
  crenellate(x0: number, z0: number, x1: number, z1: number, y: number, h: number, t: number, b: Brush, merlon = 5, gap = 4): void {
    const period = merlon + gap;
    for (let x = x0; x < x1; x += period) {
      const xe = Math.min(x1, x + merlon);
      this.box(x, y, z0, xe, y + h, z0 + t, b);
      this.box(x, y, z1 - t, xe, y + h, z1, b);
    }
    for (let z = z0; z < z1; z += period) {
      const ze = Math.min(z1, z + merlon);
      this.box(x0, y, z, x0 + t, y + h, ze, b);
      this.box(x1 - t, y, z, x1, y + h, ze, b);
    }
  }

  /** Stepped cone (sand and gravel piles, roofs): layers of `step` voxels shrinking to a point. */
  cone(cx: number, y: number, cz: number, r: number, h: number, b: Brush, step = 2): void {
    for (let dy = 0; dy < h; dy += step) {
      const rr = r * (1 - dy / h);
      if (rr < 0.8) break;
      this.cylinder(cx, cz, y + dy, Math.min(y + h, y + dy + step), rr, b);
    }
  }

  /** Upper half of a sphere (domes, igloos); `inner` > 0 hollows it out. */
  dome(cx: number, y: number, cz: number, r: number, b: Brush, inner = 0): void {
    const R = Math.ceil(r);
    const r2 = r * r;
    const i2 = inner * inner;
    for (let dy = 0; dy <= R; dy++)
      for (let dz = -R; dz <= R; dz++)
        for (let dx = -R; dx <= R; dx++) {
          const d2 = dx * dx + dy * dy + dz * dz;
          if (d2 > r2) continue;
          if (inner > 0 && d2 < i2) continue;
          this.set(cx + dx, y + dy, cz + dz, b);
        }
  }

  /**
   * Pixel-art panel on a vertical plane (neon signs, murals). Rows run top to bottom, one char per
   * pixel looked up in `colors` (other chars are skipped). (x, y, z) is the top-left pixel as seen
   * by someone facing the panel from side `face`; each pixel is px x px voxels and `depth` thick.
   */
  glyph(x: number, y: number, z: number, face: Side, rows: string[], colors: Record<string, number>, px = 1, depth = 1): void {
    for (let r = 0; r < rows.length; r++) {
      const row = rows[r];
      const ya = y - (r + 1) * px + 1;
      for (let c = 0; c < row.length; c++) {
        const v = colors[row[c]];
        if (v === undefined) continue;
        if (face === 's') this.box(x + c * px, ya, z, x + (c + 1) * px, ya + px, z + depth, v);
        else if (face === 'n') this.box(x - (c + 1) * px + 1, ya, z, x - c * px + 1, ya + px, z + depth, v);
        else if (face === 'w') this.box(x, ya, z + c * px, x + depth, ya + px, z + (c + 1) * px, v);
        else this.box(x, ya, z - (c + 1) * px + 1, x + depth, ya + px, z - c * px + 1, v);
      }
    }
  }

  // More props -----------------------------------------------------------------------------

  /** Broadleaf tree with a uniform canopy (merges into large faces, unlike speckled leaves). */
  leafyTree(x: number, y: number, z: number, height = 40, canopy = 13, leaves = M('leaves_2'), trunk = M('log_1')): void {
    this.box(x - 1, y, z - 1, x + 2, y + height, z + 2, trunk);
    const c = canopy;
    this.blob(x, y + height, z, c, c * 0.72, c, leaves, 0);
    this.blob(x + (c >> 1), y + height - (c >> 2), z - (c >> 2), c * 0.7, c * 0.55, c * 0.7, leaves, 0);
    this.blob(x - (c >> 1), y + height + (c >> 3), z + (c >> 2), c * 0.65, c * 0.55, c * 0.65, leaves, 0);
    // A couple of branches reaching into the canopy.
    this.line(x, y + height - c, z, x + (c >> 1), y + height - (c >> 2), z - (c >> 2), trunk, 2);
    this.line(x, y + height - c + 3, z, x - (c >> 1), y + height - (c >> 2), z + (c >> 2), trunk, 2);
  }

  /** Octagonal slab (union of two boxes) centered on (cx, cz): cheap round-ish tiers and columns. */
  octagon(cx: number, cz: number, y0: number, y1: number, r: number, b: Brush): void {
    const c = Math.max(0, Math.round(r * 0.5));
    this.box(cx - r, y0, cz - c, cx + r + 1, y1, cz + c + 1, b);
    this.box(cx - c, y0, cz - r, cx + c + 1, y1, cz + r + 1, b);
    if (r >= 4) {
      const d = Math.round(r * 0.8);
      this.box(cx - d, y0, cz - d, cx + d + 1, y1, cz + d + 1, b);
    }
  }

  /** Conifer of stacked octagonal tiers (few large faces) with optional snow on each tier. */
  pineTree(x: number, y: number, z: number, height = 60, snow = false, needles = M('leaves_3')): void {
    this.box(x - 1, y, z - 1, x + 2, y + height, z + 2, M('log_2'));
    const tiers = 4;
    const base = y + Math.floor(height * 0.2);
    const span = y + height - base;
    const r0 = Math.max(6, Math.floor(height / 5));
    const th = Math.floor(span / tiers) + 4;
    for (let k = 0; k < tiers; k++) {
      const ty = base + Math.floor((k * span) / tiers);
      const rt = Math.max(3, Math.round(r0 * (1 - k / (tiers + 0.5))));
      const step = Math.max(1, Math.ceil(rt / 3));
      for (let s = 0; s < 3; s++) {
        const r = Math.max(1, rt - s * step);
        const sy = ty + Math.floor((s * th) / 3);
        const ey = ty + Math.floor(((s + 1) * th) / 3);
        this.octagon(x, z, sy, ey, r, needles);
        if (snow && s === 0) this.octagon(x, z, ey, ey + 1, r - 1, M('snow_1'));
      }
    }
    this.box(x, y + height, z, x + 1, y + height + 5, z + 1, needles);
    if (snow) this.box(x, y + height + 5, z, x + 1, y + height + 6, z + 1, M('snow_1'));
  }

  /** Palm tree: a trunk curving toward `dir` and a crown of drooping fronds. */
  palm(x: number, y: number, z: number, height = 50, dir: Side = 'e', leaves = M('leaves_4')): void {
    const [dx, dz] = dir === 'e' ? [1, 0] : dir === 'w' ? [-1, 0] : dir === 's' ? [0, 1] : [0, -1];
    const lean = Math.floor(height / 5);
    for (let i = 0; i < height; i++) {
      const off = Math.floor((i * i * lean) / (height * height));
      const tx = x + dx * off;
      const tz = z + dz * off;
      this.box(tx - 1, y + i, tz - 1, tx + 1, y + i + 1, tz + 1, (i >> 2) % 2 === 0 ? M('log_1') : M('log_2'));
    }
    const cx = x + dx * lean;
    const cz = z + dz * lean;
    const ty = y + height;
    this.box(cx - 2, ty - 1, cz - 2, cx + 2, ty + 2, cz + 2, leaves);
    const dirs: [number, number][] = [[1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1]];
    for (const [fx, fz] of dirs) {
      const len = fx !== 0 && fz !== 0 ? 11 : 15;
      for (let s = 1; s <= len; s++) {
        const yy = ty + 1 - Math.floor((s * s) / 22);
        const px = cx + fx * s;
        const pz = cz + fz * s;
        const wdt = s < len - 3 ? 1 : 0;
        this.box(px - wdt, yy, pz - wdt, px + 1 + wdt, yy + 1, pz + 1 + wdt, leaves);
      }
    }
    for (const [ox, oz] of [[1, 1], [-2, 0], [0, -2]]) this.box(cx + ox, ty - 3, cz + oz, cx + ox + 2, ty - 1, cz + oz + 2, M('paint_brown'));
  }

  /** Low round shrub. */
  bush(x: number, y: number, z: number, r = 6, leaves = M('leaves_1')): void {
    this.blob(x, y + Math.floor(r * 0.4), z, r, r * 0.7, r, leaves, 0);
  }

  /** Boulder half-sunk into the ground. */
  rock(x: number, y: number, z: number, r = 8, b: Brush = M('stone_2')): void {
    this.blob(x, y + Math.floor(r / 4), z, r * 1.15, r * 0.75, r, b, 0, false);
    this.blob(x + (r >> 1), y + Math.floor(r / 2), z - (r >> 2), r * 0.6, r * 0.5, r * 0.6, b, 0, false);
  }

  /** Six-meter shipping container (60 x 26 x 24 voxels) with ribbed sides; hollow and open at one end. */
  container(x: number, y: number, z: number, alongX: boolean, color: number, open = true): void {
    const L = 60;
    const W = 24;
    const H = 26;
    const bx = alongX ? L : W;
    const bz = alongX ? W : L;
    const frame = M('steel_dark_1');
    // Body inset by one voxel on the long sides so the ribs stay inside the footprint.
    if (alongX) this.box(x, y, z + 1, x + bx, y + H, z + bz - 1, color);
    else this.box(x + 1, y, z, x + bx - 1, y + H, z + bz, color);
    // Ribs.
    for (let a = 3; a < L - 3; a += 4) {
      if (alongX) {
        this.box(x + a, y + 1, z, x + a + 2, y + H - 1, z + 1, color);
        this.box(x + a, y + 1, z + bz - 1, x + a + 2, y + H - 1, z + bz, color);
      } else {
        this.box(x, y + 1, z + a, x + 1, y + H - 1, z + a + 2, color);
        this.box(x + bx - 1, y + 1, z + a, x + bx, y + H - 1, z + a + 2, color);
      }
    }
    // Hollow interior with a plank floor.
    if (alongX) this.clear(x + 1, y + 1, z + 2, x + bx - 1, y + H - 1, z + bz - 2);
    else this.clear(x + 2, y + 1, z + 1, x + bx - 2, y + H - 1, z + bz - 1);
    // Corner posts.
    for (const [ax, az] of [
      [0, 0],
      [bx - 2, 0],
      [0, bz - 2],
      [bx - 2, bz - 2],
    ])
      this.box(x + ax, y, z + az, x + ax + 2, y + H, z + az + 2, frame);
    // Doors at the far end: open (walk-in) or closed with lock bars.
    if (alongX) {
      if (open) this.clear(x + bx - 2, y + 1, z + 2, x + bx, y + H - 2, z + bz - 2);
      else for (const zz of [z + 6, z + 10, z + 14, z + 18]) this.box(x + bx, y + 2, zz, x + bx + 1, y + H - 2, zz + 1, frame);
    } else if (open) this.clear(x + 2, y + 1, z + bz - 2, x + bx - 2, y + H - 2, z + bz);
    else for (const xx of [x + 6, x + 10, x + 14, x + 18]) this.box(xx, y + 2, z + bz, xx + 1, y + H - 2, z + bz + 1, frame);
  }

  /** Concrete pipe section lying on the ground (hollow, walk-through when r >= 10). */
  pipe(x: number, y: number, z: number, alongX: boolean, len: number, r: number, b: Brush = M('concrete_3')): void {
    const R = Math.ceil(r);
    const cy = y + R;
    for (let a = 0; a < len; a++)
      for (let dy = -R; dy <= R; dy++)
        for (let du = -R; du <= R; du++) {
          const d2 = dy * dy + du * du;
          if (d2 > r * r || d2 < (r - 2.2) * (r - 2.2)) continue;
          if (alongX) this.set(x + a, cy + dy, z + du, b);
          else this.set(x + du, cy + dy, z + a, b);
        }
  }

  /** A-frame cloth tent with an open front (toward +x or +z) and a closed back. */
  tent(x: number, y: number, z: number, alongX: boolean, len: number, width: number, cloth: number): void {
    const h = Math.floor(width / 2) + 1;
    for (let k = 0; k < h; k++) {
      const inset = Math.floor((k * width) / (2 * h));
      const a = inset;
      const b = width - inset;
      if (alongX) {
        this.box(x, y + k, z + a, x + len, y + k + 1, z + Math.min(b, a + 2), cloth);
        this.box(x, y + k, z + Math.max(a, b - 2), x + len, y + k + 1, z + b, cloth);
        this.box(x, y + k, z + a, x + 1, y + k + 1, z + b, cloth);
      } else {
        this.box(x + a, y + k, z, x + Math.min(b, a + 2), y + k + 1, z + len, cloth);
        this.box(x + Math.max(a, b - 2), y + k, z, x + b, y + k + 1, z + len, cloth);
        this.box(x + a, y + k, z, x + b, y + k + 1, z + 1, cloth);
      }
    }
    const ridge = M('log_1');
    const mid = Math.floor(width / 2);
    if (alongX) this.box(x - 1, y + h - 1, z + mid - 1, x + len + 1, y + h + 1, z + mid + 1, ridge);
    else this.box(x + mid - 1, y + h - 1, z - 1, x + mid + 1, y + h + 1, z + len + 1, ridge);
  }

  /** Sandbag wall filling a footprint; rows of 3 voxels alternate inset and shade to look stacked. */
  sandbags(x0: number, z0: number, x1: number, z1: number, y: number, h = 9): void {
    for (let k = 0; k * 3 < h; k++) {
      const inset = k % 2;
      this.box(x0 + inset, y + k * 3, z0 + inset, x1 - inset, y + k * 3 + 3, z1 - inset, inset ? M('sand_2') : M('sand_3'));
    }
  }

  /** Explosive barrel: one connected TNT cluster; the steel bands are rings flush with its surface. */
  tntBarrel(x: number, y: number, z: number): void {
    this.cylinder(x, z, y, y + 9, 3.2, M('tnt'));
    this.cylinder(x, z, y + 2, y + 3, 3.3, M('steel_dark_1'), 2.9);
    this.cylinder(x, z, y + 6, y + 7, 3.3, M('steel_dark_1'), 2.9);
    this.box(x - 1, y + 9, z - 1, x + 1, y + 10, z + 1, M('paint_yellow'));
  }

  /** Explosive crate: a TNT cube with a painted band (the core stays one connected cluster). */
  tntCrate(x: number, y: number, z: number, size = 8): void {
    this.box(x, y, z, x + size, y + size, z + size, M('tnt'));
    const mid = y + (size >> 1);
    this.walls(x, mid - 1, z, x + size, mid + 1, z + size, 1, M('paint_black'));
    this.box(x + 2, y + size, z + 2, x + size - 2, y + size + 1, z + size - 2, M('paint_yellow'));
  }

  /** Hay bale (12 x 8 x 8). */
  haybale(x: number, y: number, z: number, alongX = true): void {
    if (alongX) this.box(x, y, z, x + 12, y + 8, z + 8, M('hay'));
    else this.box(x, y, z, x + 8, y + 8, z + 12, M('hay'));
  }
}

export function isAir(w: VoxelWorld, x: number, y: number, z: number): boolean {
  return w.get(x, y, z) === AIR;
}
